import { useEffect } from 'react';

import type { PetAccessoryOffset } from '@shared/schemas/room.js';

import { useActiveChild } from '../store/sessionStore.js';
import { usePetAccessoryOffsetStore } from '../store/petAccessoryOffsetStore.js';

/**
 * Độ lệch của phụ kiện ĐANG MẶC — cùng khuôn với `useRoomPlacements`.
 *
 * ⚠️ `save` chỉ thật sự gửi khi `childId` của store KHỚP bé đang chọn. Đây là chốt chống một
 *    response/lệnh ghi của bé trước rơi vào hồ sơ bé sau khi phụ huynh đổi bé giữa lúc kéo.
 */
export function useAccessoryOffsets(): {
  childId: string | null;
  offsets: PetAccessoryOffset[] | null;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  save: (offsets: PetAccessoryOffset[], targetChildId?: string) => Promise<void>;
  reload: () => Promise<void>;
} {
  const activeChild = useActiveChild();
  const childId = usePetAccessoryOffsetStore((state) => state.childId);
  const offsets = usePetAccessoryOffsetStore((state) => state.offsets);
  const isLoading = usePetAccessoryOffsetStore((state) => state.loading);
  const isSaving = usePetAccessoryOffsetStore((state) => state.saving);
  const error = usePetAccessoryOffsetStore((state) => state.error);
  const load = usePetAccessoryOffsetStore((state) => state.load);
  const saveToServer = usePetAccessoryOffsetStore((state) => state.save);
  const reset = usePetAccessoryOffsetStore((state) => state.reset);

  useEffect(() => {
    if (activeChild) void load(activeChild.id);
    else reset();
  }, [activeChild, load, reset]);

  return {
    childId,
    offsets,
    isLoading,
    isSaving,
    error,
    save: async (next, targetChildId) => {
      if (activeChild && targetChildId === activeChild.id && childId === activeChild.id) {
        await saveToServer(activeChild.id, next);
      }
    },
    reload: async () => {
      if (activeChild) await load(activeChild.id);
    },
  };
}
